/*
  insole-quiz.js — behaviour for the Blumaka insole finder (/pages/quiz).

  Rules live in snippets/insole-quiz-config.liquid. Variant data is rendered by
  sections/insole-quiz.liquid at page load. This file only reads them.

  Resolution: product + profile from the activity, arch from the foot need,
  then raised (never lowered) by the activity's arch floor. Two foot needs
  override the product to the Carbon Elite outright.

  Add to cart uses the theme's own window._ajaxAddToCart + _updateCartTotals so
  the slide-out drawer opens and repopulates exactly as it does everywhere else.
*/
(function () {
  'use strict';

  /* The quiz is booted two ways: as a section on /pages/quiz, where it is in the
     DOM at parse time, and inside the homepage launcher, where the section HTML
     is fetched and injected on first click. So the whole thing is a function of
     its root element rather than a page-load side effect, and
     window.BlumakaInsoleQuiz.init(root) drives the second case. */
  function boot(root) {
    if (!root || root.hasAttribute('data-iq-ready')) return false;
    root.setAttribute('data-iq-ready', '');

    function readJSON(sel) {
      var el = root.querySelector(sel);
      if (!el) return null;
      try { return JSON.parse(el.textContent); }
      catch (e) { console.error('[insole-quiz] bad JSON in ' + sel, e); return null; }
    }

    var CFG = readJSON('[data-iq-config]');
    var CAT = readJSON('[data-iq-catalog]');
    var SET = readJSON('[data-iq-settings]') || {};
    if (!CFG || !CAT) return;

    var $  = function (s) { return root.querySelector(s); };
    var RANK = { Low: 0, Medium: 1, High: 2 };
    var PROFILES = ['Max Cushion', 'Low Profile'];
    var ARCHES = ['Low', 'Medium', 'High'];
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ---------- variant index ---------- */
    // key "productKey|profile|arch|sizeValue" -> variant
    var INDEX = {};
    Object.keys(CAT).forEach(function (k) {
      (CAT[k].variants || []).forEach(function (v) {
        INDEX[[k, v.p || '-', v.a || '-', v.s].join('|')] = v;
      });
    });
    // A product sold without a Profile option (the Wide Toe Box: Arch Support and
    // Size only) is indexed with profile "-", so it needs no build question and no
    // build chips. Derived from the catalogue, so no product key is hardcoded here.
    var NOPROF = {};
    Object.keys(CAT).forEach(function (k) {
      NOPROF[k] = k !== 'ce' && !(CAT[k].variants || []).some(function (v) { return v.p; });
    });
    function lookup(pk, prof, arch, sizeIdx) {
      var size = CFG.sizes[sizeIdx];
      if (!size) return null;
      return INDEX[[pk, prof || '-', arch || '-', size.value].join('|')] || null;
    }

    /* ---------- state ---------- */
    function freshAnswers() {
      return { size: null, act: null, leaf: null, need: null, product: null,
               profile: null, arch: null, archRaised: false, fallback: null,
               fallbackReason: null, want: null,
               pack: 1, pairs: [], packParent: null, bundleId: null,
               edited: false, actLabel: null, leafLabel: null, needLabel: null,
               ceAlt: false, wtb: false };
    }
    var A = freshAnswers();
    var history = ['welcome'];
    var PCT = { welcome: 0, size: 25, act: 50, sub: 62, need: 80, load: 92, result: 100 };

    /* ---------- analytics ---------- */
    // Bump when the decision table or question set changes, so results stay comparable.
    // v3-2026-10-05: the Carbon Elite result screen says why it costs more and offers
    // the activity's own insole beside it (RR-024). Decision table unchanged.
    // v4-2026-10-16: Wide Toe Box added as a "My shoes have a wide toe box" toggle on the
    // size screen (product key wtb, every activity except Cleated Sports; a turf toe or
    // Lisfranc need still goes to the Carbon Elite, which then adds a standard-width
    // line). The Performance result's third benefit changed from the retired "100%
    // recycled top cover" to the energy-return claim. Every other route is unchanged from
    // v3 (RR-024 reads v3 and v4 and leaves out runs whose product is the WTB insole).
    var QUIZ_VERSION = 'v4-2026-10-16';

    // One id per quiz run. Stamped on every event AND on the cart line item, so a
    // completed quiz can be joined to the order it produced without any backend.
    function newQuizId() {
      var r = '';
      try {
        if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
      } catch (e) {}
      for (var i = 0; i < 4; i++) r += Math.random().toString(36).slice(2, 10);
      return r.slice(0, 32);
    }
    var QID = newQuizId();

    // A fresh id per POST, distinct from the quiz id. Nothing prevented duplicate
    // rows before: an Apps Script doPost that times out and gets retried writes the
    // row twice, and a double counted add_to_cart inflates the only conversion
    // metric the quiz has. The collector deduplicates on this.
    function newEventId() { return newQuizId(); }

    // Whether the email offer was on screen. Without this, "declined" and "never
    // asked" are indistinguishable in the sheet and consent rate is unmeasurable.
    var OFFER_SHOWN = root.getAttribute('data-offer-enabled') === 'true';

    // Landing context, captured once per session and kept in sessionStorage so it
    // survives navigation. Deliberately separate from `referrer`, which records
    // which page the quiz was opened from and cannot answer where the visitor came
    // from. NOTE: this script only loads on pages that host a quiz, so entry_path
    // is the first quiz-bearing page of the session, NOT necessarily the first page
    // the visitor saw. True first touch has to come from the order.
    var ENTRY = (function () {
      var KEY = 'blumaka_quiz_entry_v1';
      try {
        var saved = sessionStorage.getItem(KEY);
        if (saved) return JSON.parse(saved);
      } catch (e) {}
      var q = {};
      try {
        new URLSearchParams(location.search).forEach(function (v, k) { q[k.toLowerCase()] = v; });
      } catch (e) {}
      var entry = {
        utm_source: q.utm_source || null,
        utm_medium: q.utm_medium || null,
        utm_campaign: q.utm_campaign || null,
        utm_content: q.utm_content || null,
        path: location.pathname || null
      };
      try { sessionStorage.setItem(KEY, JSON.stringify(entry)); } catch (e) {}
      return entry;
    })();

    // Tablets were landing silently in the wrong bucket: iPadOS reports a Macintosh
    // user agent so iPads read as desktop, while Android tablets read as mobile.
    // Device is a plausible driver of abandonment and a merged bucket hides it.
    function deviceType() {
      var ua = navigator.userAgent || '';
      var touchMac = /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
      if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || touchMac ||
          (/Android/.test(ua) && !/Mobile/.test(ua))) return 'tablet';
      if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
      return 'desktop';
    }

    // The size label is a compound string. THREE sizes are men's only and carry no
    // " | Women's " half at all (15-15.5, 16-16.5, 17-17.5), so anything splitting
    // on the delimiter gets two fields for most rows and one for those. Emit the
    // parts and never ask a consumer to parse the display string.
    function sizeParts(i) {
      if (i == null || !CFG.sizes[i]) return { display: null, mens: null, womens: null };
      var label = CFG.sizes[i].label;
      var bits = label.split(' | ');
      return { display: label, mens: bits[0] || null, womens: bits[1] || null };
    }

    // Stages worth a row in the repository. quiz_email_submitted is deliberately
    // absent: it is recorded separately so the email address reaches the collector
    // without ever being pushed into dataLayer or GTM.
    // pack_selected is here so the sheet can see people who CONSIDERED a pack and
    // did not buy one. Without it the only evidence of the bundle is on orders, and
    // "does the bundle lift AOV" needs the denominator, not just the numerator.
    var RECORD_STAGES = { quiz_completed: 1, quiz_abandoned: 1, quiz_add_to_cart: 1,
                          quiz_pack_selected: 1 };

    function push(event, props) {
      var payload = Object.assign({ event: event, quiz_id: QID }, props || {});
      try { (window.dataLayer = window.dataLayer || []).push(payload); } catch (e) {}
      try {
        if (window._learnq) {
          window._learnq.push(['track', event.replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }), props || {}]);
        }
      } catch (e) {}
      if (RECORD_STAGES[event]) { record(event.replace(/^quiz_/, ''), { last_question: payload.last_question || null }); }
    }
    /* ---------- response repository ----------
       One JSON row per completed or abandoned quiz, POSTed to whatever endpoint is
       set in the section settings. This is the only thing that captures the people
       who take the quiz and DON'T buy - the line-item properties can only ever see
       converters, and nothing here can be backfilled.

       Deliberately endpoint-agnostic: a Google Apps Script bound to a Sheet, a
       Cloudflare Worker, or any POST endpoint all take the same body. Sent as
       text/plain so the browser skips the CORS preflight, which Apps Script cannot
       answer, and via sendBeacon first so the abandonment row survives the page
       being closed. Fire and forget - a dead endpoint never affects the customer. */
    // Outbox. The payload is parked in sessionStorage before it is sent, so a tab
    // closed mid-flight replays the row on the next page load instead of losing it.
    // A dropped row does not merely cost a number, it BIASES the funnel, because
    // failures cluster on slow mobile connections and mobile is most of this traffic.
    var OUTBOX_KEY = 'blumaka_quiz_outbox_v1';
    function outboxRead() {
      try { return JSON.parse(sessionStorage.getItem(OUTBOX_KEY) || '[]'); } catch (e) { return []; }
    }
    function outboxWrite(a) {
      try { sessionStorage.setItem(OUTBOX_KEY, JSON.stringify(a.slice(-20))); } catch (e) {}
    }
    function outboxDrop(id) {
      outboxWrite(outboxRead().filter(function (r) { return r && r.event_id !== id; }));
    }

    // Every attempt carries the SAME event_id, so an over-eager retry costs nothing
    // once the collector deduplicates on it. Retries fire only when the promise
    // REJECTS, which means the request never reached the network, so a retry cannot
    // duplicate a row the server already holds.
    function send(url, row, attempt) {
      var body = JSON.stringify(row);
      attempt = attempt || 0;
      try {
        if (attempt === 0 && navigator.sendBeacon &&
            navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) {
          outboxDrop(row.event_id);
          return;
        }
      } catch (e) {}
      try {
        fetch(url, {
          method: 'POST', mode: 'no-cors', keepalive: true,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: body
        }).then(function () {
          outboxDrop(row.event_id);
        })['catch'](function () {
          if (attempt < 2) {
            window.setTimeout(function () { send(url, row, attempt + 1); }, 800 * Math.pow(2, attempt));
          }
        });
      } catch (e) {}
    }

    // Pack accessors used by record(). Deliberately defensive: the abandoned row is
    // written from pagehide, which can fire before any of this state exists.
    function packRowSize() {
      try { return (A.pack > 1 && packComplete()) ? A.pack : 1; } catch (e) { return 1; }
    }
    function packRowActivities() {
      try {
        if (A.pack < 2 || A.product === 'ce') return null;
        var a = activePairs().map(function (p) { return p.leaf; });
        return a.length ? a.join(',') : null;
      } catch (e) { return null; }
    }
    function packRowPrice() {
      try {
        var cur = A.product ? lookup(A.product, A.profile, A.arch, A.size) : null;
        if (!cur) return null;
        if (!(A.pack > 1 && packComplete())) return cur.price;
        return quoteFor(A.pack, A.product === 'ce', cur).net;
      } catch (e) { return null; }
    }

    var RECORDED = {};
    function record(stage, extra) {
      var url = (SET.collectorUrl || '').trim();
      if (!url || RECORDED[stage]) return;
      RECORDED[stage] = true;

      var cur = A.product ? lookup(A.product, A.profile, A.arch, A.size) : null;
      var sz = sizeParts(A.size);
      var row = Object.assign({
        event_id: newEventId(),
        quiz_id: QID,
        quiz_version: QUIZ_VERSION,
        stage: stage,
        ts: new Date().toISOString(),
        // Which entry point this run came from. Once the homepage bubble exists
        // the two surfaces are otherwise indistinguishable in the sheet, and
        // comparing them is the whole point of having a second one.
        source: root.closest('[data-iq-modal]') ? 'homepage-bubble' : 'quiz-page',
        page: location.pathname,
        size: sz.display,
        size_mens: sz.mens,
        size_womens: sz.womens,
        activity_category: A.act || null,
        activity: A.leafLabel || A.leaf || null,
        foot_need: A.needLabel || A.need || null,
        product: A.product ? CAT[A.product].title : null,
        profile: A.profile || null,
        arch: A.arch || null,
        arch_raised: !!A.archRaised,
        fallback: A.fallback || null,
        edited: !!A.edited,
        variant_id: cur ? cur.id : null,
        price_cents: cur ? cur.price : null,
        in_stock: cur ? !!cur.av : null,
        // The pack, as it stands at the moment this row is written. packProps() only
        // ever reached dataLayer; record() forwards last_question and nothing else.
        pack_size: packRowSize(),
        pack_activities: packRowActivities(),
        pack_price_cents: packRowPrice(),
        bundle_id: A.bundleId || null,
        referrer: document.referrer || null,
        mobile: /Mobi|Android/i.test(navigator.userAgent),
        device_type: deviceType(),
        entry_utm_source: ENTRY.utm_source,
        entry_utm_medium: ENTRY.utm_medium,
        entry_utm_campaign: ENTRY.utm_campaign,
        entry_utm_content: ENTRY.utm_content,
        entry_path: ENTRY.path,
        // false means asked and not given, null means never asked. The email
        // submission overrides this with true through `extra`.
        email_consent: (OFFER_SHOWN && stage !== 'abandoned') ? false : null
      }, extra || {});

      outboxAdd(row);
      send(url, row, 0);
    }

    function outboxAdd(row) { var a = outboxRead(); a.push(row); outboxWrite(a); }

    // Replay anything an earlier page left unsent. attempt 1 skips sendBeacon so the
    // fetch promise can confirm delivery and clear the row.
    (function drainOutbox() {
      var url = (SET.collectorUrl || '').trim();
      if (!url) return;
      outboxRead().forEach(function (row) { if (row && row.event_id) send(url, row, 1); });
    })();

    function answerProps() {
      var cur = A.product ? lookup(A.product, A.profile, A.arch, A.size) : null;
      return {
        quiz_size: A.size != null ? CFG.sizes[A.size].label : null,
        quiz_activity_category: A.act,
        quiz_activity: A.leaf,
        quiz_foot_need: A.need,
        quiz_recommended_product: A.product ? CAT[A.product].title : null,
        quiz_profile: A.profile,
        quiz_arch: A.arch,
        quiz_variant_id: cur ? cur.id : null,
        quiz_wide_toe_box: !!A.wtb
      };
    }

    /* ---------- screens ---------- */
    function show(id) {
      root.querySelectorAll('[data-iq-screen]').forEach(function (s) {
        s.classList.toggle('is-on', s.getAttribute('data-iq-screen') === id);
      });
      var bar = $('[data-iq-bar]');
      if (bar) bar.style.width = PCT[id] + '%';
      var pr = root.querySelector('.iq__prog');
      if (pr) pr.setAttribute('aria-valuenow', PCT[id]);
      var head = root.querySelector('[data-iq-screen="' + id + '"] .iq__q');
      if (head) { head.setAttribute('tabindex', '-1'); head.focus({ preventScroll: true }); }
    }
    function go(id) { history.push(id); show(id); if (id !== 'load' && id !== 'result') push('quiz_question_viewed', { question_id: id, question_index: history.length - 1 }); }
    function back() {
      if (history.length < 2) return;
      var from = history.pop();
      push('quiz_back', { from_question: from });
      show(history[history.length - 1]);
    }

    /* ---------- build the questions ---------- */
    /*
      Icon set. Sport, object and activity marks are Tabler Icons (MIT licence,
      https://tabler.io/icons) - copyright (c) 2020-2024 Pawel Kuna. The foot-need
      marks, the lacrosse stick and the pickleball paddle are drawn for Blumaka in
      the same 24x24 / 1.8-stroke language, because Tabler has no equivalent.
      All are stroked with currentColor so CSS supplies the accent colour.
    */
    var SVG = function (body) {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + body + '</svg>';
    };
    var ICON_PATHS = {
      run: '<path d="M11.007 5a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /> <path d="M4 17l5 1l.75 -1.5" /> <path d="M15 21v-4l-4 -3l1 -6" /> <path d="M7 12v-3l5 -1l3 3l3 1" />',
      hike: '<path d="M11 4a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" /> <path d="M7 21l2 -4" /> <path d="M13 21v-4l-3 -3l1 -6l3 4l3 2" /> <path d="M10 14l-1.827 -1.218a2 2 0 0 1 -.831 -2.15l.28 -1.117a2 2 0 0 1 1.939 -1.515h1.439l4 1l3 -2" /> <path d="M17 12v9" /> <path d="M16 20h2" />',
      court: '<path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /> <path d="M5.65 5.65l12.7 12.7" /> <path d="M5.65 18.35l12.7 -12.7" /> <path d="M12 3a9 9 0 0 0 9 9" /> <path d="M3 12a9 9 0 0 1 9 9" />',
      cleat: '<path d="M9 12a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /> <path d="M3 9h3v6h-3l0 -6" /> <path d="M18 9h3v6h-3l0 -6" /> <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10" /> <path d="M12 5l0 14" />',
      golf: '<path d="M12 18v-15l7 4l-7 4" /> <path d="M9 17.67c-.62 .36 -1 .82 -1 1.33c0 1.1 1.8 2 4 2s4 -.9 4 -2c0 -.5 -.38 -.97 -1 -1.33" />',
      gym: '<path d="M2 12h1" /> <path d="M6 8h-2a1 1 0 0 0 -1 1v6a1 1 0 0 0 1 1h2" /> <path d="M6 7v10a1 1 0 0 0 1 1h1a1 1 0 0 0 1 -1v-10a1 1 0 0 0 -1 -1h-1a1 1 0 0 0 -1 1" /> <path d="M9 12h6" /> <path d="M15 7v10a1 1 0 0 0 1 1h1a1 1 0 0 0 1 -1v-10a1 1 0 0 0 -1 -1h-1a1 1 0 0 0 -1 1" /> <path d="M18 8h2a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-2" /> <path d="M22 12h-1" />',
      work: '<path d="M3 9a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2l0 -9" /> <path d="M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2" /> <path d="M12 12l0 .01" /> <path d="M3 13a20 20 0 0 0 18 0" />',
      day: '<path d="M4 6h5.426a1 1 0 0 1 .863 .496l1.064 1.823a3 3 0 0 0 1.896 1.407l4.677 1.114a4 4 0 0 1 3.074 3.89v2.27a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1v-10a1 1 0 0 1 1 -1" /> <path d="M14 13l1 -2" /> <path d="M8 18v-1a4 4 0 0 0 -4 -4h-1" /> <path d="M10 12l1.5 -3" />',
      road: '<path d="M4 19l4 -14" /> <path d="M16 5l4 14" /> <path d="M12 8v-2" /> <path d="M12 13v-2" /> <path d="M12 18v-2" />',
      trail: '<path d="M3 20h18l-6.921 -14.612a2.3 2.3 0 0 0 -4.158 0l-6.921 14.612" /> <path d="M7.5 11l2 2.5l2.5 -2.5l2 3l2.5 -2" />',
      track: '<path d="M4 12a8 2 0 1 0 16 0a8 2 0 1 0 -16 0" /> <path d="M4 12v7c0 .94 2.51 1.785 6 2v-3h4v3c3.435 -.225 6 -1.07 6 -2v-7" /> <path d="M15 6h4v-3h-4v7" /> <path d="M7 6h4v-3h-4v7" />',
      xc: '<path d="M5 14h14l-4.5 -4.5l4.5 -4.5h-14v16" />',
      basketball: '<path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /> <path d="M5.65 5.65l12.7 12.7" /> <path d="M5.65 18.35l12.7 -12.7" /> <path d="M12 3a9 9 0 0 0 9 9" /> <path d="M3 12a9 9 0 0 1 9 9" />',
      volleyball: '<path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /> <path d="M12 12a8 8 0 0 0 8 4" /> <path d="M7.5 13.5a12 12 0 0 0 8.5 6.5" /> <path d="M12 12a8 8 0 0 0 -7.464 4.928" /> <path d="M12.951 7.353a12 12 0 0 0 -9.88 4.111" /> <path d="M12 12a8 8 0 0 0 -.536 -8.928" /> <path d="M15.549 15.147a12 12 0 0 0 1.38 -10.611" />',
      pickleball: '<rect x="2.6" y="2.8" width="10.6" height="9.4" rx="2.6"/><path d="M7.9 12.2v3"/><rect x="6.3" y="15.2" width="3.2" height="5.4" rx="1.6"/><circle cx="18" cy="16.4" r="4"/><path d="M16.6 15h.01M19.4 15h.01M18 18.2h.01"/>',
      tennis: '<path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /> <path d="M6 5.3a9 9 0 0 1 0 13.4" /> <path d="M18 5.3a9 9 0 0 0 0 13.4" />',
      football: '<path d="M15 9l-6 6" /> <path d="M10 12l2 2" /> <path d="M12 10l2 2" /> <path d="M8 21a5 5 0 0 0 -5 -5" /> <path d="M16 3c-7.18 0 -13 5.82 -13 13a5 5 0 0 0 5 5c7.18 0 13 -5.82 13 -13a5 5 0 0 0 -5 -5" /> <path d="M16 3a5 5 0 0 0 5 5" />',
      baseball: '<path d="M5.636 18.364a9 9 0 1 0 12.728 -12.728a9 9 0 0 0 -12.728 12.728" /> <path d="M12.495 3.02a9 9 0 0 1 -9.475 9.475" /> <path d="M20.98 11.505a9 9 0 0 0 -9.475 9.475" /> <path d="M9 9l2 2" /> <path d="M13 13l2 2" /> <path d="M11 7l2 1" /> <path d="M7 11l1 2" /> <path d="M16 11l1 2" /> <path d="M11 16l2 1" />',
      soccer: '<path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /> <path d="M12 7l4.76 3.45l-1.76 5.55h-6l-1.76 -5.55l4.76 -3.45" /> <path d="M12 7v-4m3 13l2.5 3m-.74 -8.55l3.74 -1.45m-11.44 7.05l-2.56 2.95m.74 -8.55l-3.74 -1.45" />',
      lacrosse: '<path d="M9.4 3.2h9.2c.95 0 1.65.9 1.42 1.82l-1.4 5.5c-.36 1.42-1.64 2.42-3.1 2.42h-3.04c-1.46 0-2.74-1-3.1-2.42l-1.4-5.5C7.75 4.1 8.45 3.2 9.4 3.2z"/><path d="M11.1 3.2l1.5 9.74M16.9 3.2l-1.5 9.74M8.5 7.6h11"/><path d="M14 12.94V21"/>',
      sneaker: '<path d="M4 6h5.426a1 1 0 0 1 .863 .496l1.064 1.823a3 3 0 0 0 1.896 1.407l4.677 1.114a4 4 0 0 1 3.074 3.89v2.27a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1v-10a1 1 0 0 1 1 -1" /> <path d="M14 13l1 -2" /> <path d="M8 18v-1a4 4 0 0 0 -4 -4h-1" /> <path d="M10 12l1.5 -3" />',
      dressshoe: '<path d="M14 6a2 2 0 1 0 -4 0c0 1.667 .67 3 2 4h-.008l7.971 4.428a2 2 0 0 1 1.029 1.749v.823a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-.823a2 2 0 0 1 1.029 -1.749l7.971 -4.428" />',
      comfort: '<path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" /> <path d="M12 7v5l3 3" />',
      arch: '<path d="M9.47 6.5C9.49 6.7 9.54 7.32 9.62 7.71C9.69 8.11 9.78 8.51 9.92 8.88C10.06 9.25 10.23 9.62 10.44 9.96C10.65 10.3 10.91 10.64 11.19 10.94C11.47 11.24 11.82 11.49 12.14 11.76C12.46 12.02 12.8 12.26 13.11 12.53C13.43 12.8 13.73 13.12 14.05 13.39C14.37 13.66 14.68 13.94 15.02 14.16C15.36 14.39 15.71 14.6 16.08 14.72C16.46 14.85 16.88 14.87 17.29 14.94C17.69 15.01 18.1 15.14 18.5 15.13C18.9 15.13 19.36 14.84 19.69 14.92C20.02 15 20.48 15.34 20.49 15.6C20.5 15.87 20.05 16.31 19.73 16.51C19.41 16.71 18.95 16.69 18.56 16.79C18.16 16.88 17.77 16.97 17.38 17.08C17 17.19 16.64 17.4 16.25 17.46C15.85 17.52 15.4 17.5 15 17.44C14.6 17.37 14.24 17.21 13.87 17.06C13.5 16.91 13.16 16.69 12.78 16.55C12.41 16.42 12.01 16.35 11.61 16.26C11.21 16.18 10.81 16.12 10.4 16.06C10 16 9.59 15.9 9.18 15.89C8.77 15.89 8.36 15.96 7.96 16.03C7.56 16.1 7.18 16.26 6.78 16.3C6.37 16.35 5.92 16.37 5.52 16.3C5.13 16.24 4.71 16.12 4.4 15.91C4.08 15.69 3.76 15.36 3.62 15.01C3.48 14.66 3.52 14.19 3.58 13.8C3.63 13.41 3.87 13.06 3.97 12.67C4.07 12.28 4.15 11.88 4.2 11.47C4.24 11.06 4.25 10.63 4.24 10.21C4.24 9.79 4.22 9.36 4.18 8.94C4.15 8.53 4.09 8.12 4.03 7.71C3.98 7.31 3.88 6.7 3.85 6.5" stroke-width="1.5"/><path d="M5.9 11.5a1.35 1.35 0 0 0 1.55 1.2" stroke-width="1.5"/><path d="M11.52 18.3C11.46 18.28 11.24 18.22 11.14 18.2C11.04 18.18 11.04 18.19 10.94 18.18C10.84 18.16 10.67 18.13 10.54 18.11C10.41 18.09 10.24 18.06 10.14 18.04C10.05 18.03 10.07 18.03 9.98 18.01C9.88 18 9.71 17.98 9.58 17.96C9.44 17.94 9.27 17.91 9.18 17.9C9.09 17.9 9.12 17.91 9.03 17.92C8.94 17.93 8.77 17.95 8.63 17.96C8.5 17.98 8.35 17.99 8.23 18.01C8.11 18.03 8.04 18.05 7.93 18.08C7.81 18.1 7.67 18.13 7.54 18.16C7.41 18.19 7.33 18.23 7.15 18.25C6.97 18.28 6.58 18.29 6.46 18.3" stroke-width="1.35"/>',
      heel: '<path d="M9.47 6.5C9.49 6.7 9.54 7.32 9.62 7.71C9.69 8.11 9.78 8.51 9.92 8.88C10.06 9.25 10.23 9.62 10.44 9.96C10.65 10.3 10.91 10.64 11.19 10.94C11.47 11.24 11.82 11.49 12.14 11.76C12.46 12.02 12.8 12.26 13.11 12.53C13.43 12.8 13.73 13.12 14.05 13.39C14.37 13.66 14.68 13.94 15.02 14.16C15.36 14.39 15.71 14.6 16.08 14.72C16.46 14.85 16.88 14.87 17.29 14.94C17.69 15.01 18.1 15.14 18.5 15.13C18.9 15.13 19.36 14.84 19.69 14.92C20.02 15 20.48 15.34 20.49 15.6C20.5 15.87 20.05 16.31 19.73 16.51C19.41 16.71 18.95 16.69 18.56 16.79C18.16 16.88 17.77 16.97 17.38 17.08C17 17.19 16.64 17.4 16.25 17.46C15.85 17.52 15.4 17.5 15 17.44C14.6 17.37 14.24 17.21 13.87 17.06C13.5 16.91 13.16 16.69 12.78 16.55C12.41 16.42 12.01 16.35 11.61 16.26C11.21 16.18 10.81 16.12 10.4 16.06C10 16 9.59 15.9 9.18 15.89C8.77 15.89 8.36 15.96 7.96 16.03C7.56 16.1 7.18 16.26 6.78 16.3C6.37 16.35 5.92 16.37 5.52 16.3C5.13 16.24 4.71 16.12 4.4 15.91C4.08 15.69 3.76 15.36 3.62 15.01C3.48 14.66 3.52 14.19 3.58 13.8C3.63 13.41 3.87 13.06 3.97 12.67C4.07 12.28 4.15 11.88 4.2 11.47C4.24 11.06 4.25 10.63 4.24 10.21C4.24 9.79 4.22 9.36 4.18 8.94C4.15 8.53 4.09 8.12 4.03 7.71C3.98 7.31 3.88 6.7 3.85 6.5" stroke-width="1.5"/><path d="M5.9 11.5a1.35 1.35 0 0 0 1.55 1.2" stroke-width="1.5"/><path d="M3.66 17.77L3.21 18.88" stroke-width="1.35"/><path d="M2.23 16.46L1.29 17.2" stroke-width="1.35"/><path d="M1.62 14.65L0.43 14.81" stroke-width="1.35"/>',
      flat: '<path d="M9.47 6.5C9.5 6.7 9.55 7.31 9.62 7.71C9.69 8.11 9.78 8.51 9.92 8.88C10.06 9.26 10.23 9.62 10.44 9.96C10.65 10.3 10.91 10.64 11.19 10.94C11.47 11.24 11.82 11.5 12.14 11.76C12.46 12.03 12.79 12.26 13.11 12.53C13.43 12.8 13.73 13.12 14.05 13.39C14.37 13.66 14.68 13.94 15.02 14.16C15.36 14.38 15.7 14.59 16.08 14.72C16.46 14.85 16.89 14.87 17.29 14.94C17.69 15.01 18.1 15.13 18.5 15.13C18.9 15.13 19.36 14.84 19.69 14.92C20.02 15 20.48 15.34 20.49 15.6C20.5 15.87 20.05 16.31 19.73 16.51C19.41 16.71 18.95 16.7 18.56 16.79C18.17 16.88 17.77 16.97 17.38 17.08C16.99 17.19 16.65 17.41 16.25 17.46C15.85 17.51 15.4 17.41 15 17.4C14.6 17.39 14.24 17.4 13.87 17.4C13.5 17.4 13.16 17.4 12.78 17.4C12.4 17.4 12.01 17.4 11.61 17.4C11.21 17.4 10.81 17.4 10.4 17.4C10 17.4 9.59 17.4 9.18 17.4C8.77 17.4 8.36 17.4 7.96 17.4C7.56 17.4 7.19 17.4 6.78 17.4C6.37 17.4 5.92 17.44 5.52 17.4C5.12 17.36 4.72 17.57 4.4 17.18C4.08 16.78 3.76 15.57 3.62 15.01C3.48 14.45 3.52 14.19 3.58 13.8C3.64 13.41 3.87 13.06 3.97 12.67C4.07 12.28 4.16 11.88 4.2 11.47C4.25 11.06 4.24 10.63 4.24 10.21C4.24 9.79 4.22 9.36 4.18 8.94C4.14 8.52 4.09 8.12 4.03 7.71C3.98 7.3 3.88 6.7 3.85 6.5" stroke-width="1.5"/><path d="M5.9 11.5a1.35 1.35 0 0 0 1.55 1.2" stroke-width="1.5"/><path d="M4 19.15L17.4 19.15" stroke-width="1.35"/>',
      midfoot: '<path d="M9.47 6.5C9.49 6.7 9.54 7.32 9.62 7.71C9.69 8.11 9.78 8.51 9.92 8.88C10.06 9.25 10.23 9.62 10.44 9.96C10.65 10.3 10.91 10.64 11.19 10.94C11.47 11.24 11.82 11.49 12.14 11.76C12.46 12.02 12.8 12.26 13.11 12.53C13.43 12.8 13.73 13.12 14.05 13.39C14.37 13.66 14.68 13.94 15.02 14.16C15.36 14.39 15.71 14.6 16.08 14.72C16.46 14.85 16.88 14.87 17.29 14.94C17.69 15.01 18.1 15.14 18.5 15.13C18.9 15.13 19.36 14.84 19.69 14.92C20.02 15 20.48 15.34 20.49 15.6C20.5 15.87 20.05 16.31 19.73 16.51C19.41 16.71 18.95 16.69 18.56 16.79C18.16 16.88 17.77 16.97 17.38 17.08C17 17.19 16.64 17.4 16.25 17.46C15.85 17.52 15.4 17.5 15 17.44C14.6 17.37 14.24 17.21 13.87 17.06C13.5 16.91 13.16 16.69 12.78 16.55C12.41 16.42 12.01 16.35 11.61 16.26C11.21 16.18 10.81 16.12 10.4 16.06C10 16 9.59 15.9 9.18 15.89C8.77 15.89 8.36 15.96 7.96 16.03C7.56 16.1 7.18 16.26 6.78 16.3C6.37 16.35 5.92 16.37 5.52 16.3C5.13 16.24 4.71 16.12 4.4 15.91C4.08 15.69 3.76 15.36 3.62 15.01C3.48 14.66 3.52 14.19 3.58 13.8C3.63 13.41 3.87 13.06 3.97 12.67C4.07 12.28 4.15 11.88 4.2 11.47C4.24 11.06 4.25 10.63 4.24 10.21C4.24 9.79 4.22 9.36 4.18 8.94C4.15 8.53 4.09 8.12 4.03 7.71C3.98 7.31 3.88 6.7 3.85 6.5" stroke-width="1.5"/><path d="M5.9 11.5a1.35 1.35 0 0 0 1.55 1.2" stroke-width="1.5"/><path d="M13.64 10.4C13.67 10.43 13.79 10.52 13.87 10.58C13.95 10.64 14.03 10.71 14.11 10.77C14.18 10.83 14.27 10.89 14.34 10.96C14.42 11.02 14.48 11.08 14.55 11.14C14.62 11.21 14.7 11.28 14.78 11.35C14.86 11.42 14.93 11.49 15.01 11.56C15.08 11.63 15.18 11.71 15.24 11.77C15.3 11.82 15.31 11.84 15.38 11.89C15.44 11.94 15.53 12.01 15.61 12.08C15.69 12.14 15.77 12.2 15.85 12.26C15.93 12.33 16.06 12.43 16.08 12.45C16.11 12.48 15.97 12.4 16 12.42C16.03 12.44 16.18 12.51 16.26 12.56C16.35 12.6 16.48 12.67 16.52 12.69" stroke-width="1.35"/>',
      toe: '<path d="M9.47 6.5C9.49 6.7 9.54 7.32 9.62 7.71C9.69 8.11 9.78 8.51 9.92 8.88C10.06 9.25 10.23 9.62 10.44 9.96C10.65 10.3 10.91 10.64 11.19 10.94C11.47 11.24 11.82 11.49 12.14 11.76C12.46 12.02 12.8 12.26 13.11 12.53C13.43 12.8 13.73 13.12 14.05 13.39C14.37 13.66 14.68 13.94 15.02 14.16C15.36 14.39 15.71 14.6 16.08 14.72C16.46 14.85 16.88 14.87 17.29 14.94C17.69 15.01 18.1 15.14 18.5 15.13C18.9 15.13 19.36 14.84 19.69 14.92C20.02 15 20.48 15.34 20.49 15.6C20.5 15.87 20.05 16.31 19.73 16.51C19.41 16.71 18.95 16.69 18.56 16.79C18.16 16.88 17.77 16.97 17.38 17.08C17 17.19 16.64 17.4 16.25 17.46C15.85 17.52 15.4 17.5 15 17.44C14.6 17.37 14.24 17.21 13.87 17.06C13.5 16.91 13.16 16.69 12.78 16.55C12.41 16.42 12.01 16.35 11.61 16.26C11.21 16.18 10.81 16.12 10.4 16.06C10 16 9.59 15.9 9.18 15.89C8.77 15.89 8.36 15.96 7.96 16.03C7.56 16.1 7.18 16.26 6.78 16.3C6.37 16.35 5.92 16.37 5.52 16.3C5.13 16.24 4.71 16.12 4.4 15.91C4.08 15.69 3.76 15.36 3.62 15.01C3.48 14.66 3.52 14.19 3.58 13.8C3.63 13.41 3.87 13.06 3.97 12.67C4.07 12.28 4.15 11.88 4.2 11.47C4.24 11.06 4.25 10.63 4.24 10.21C4.24 9.79 4.22 9.36 4.18 8.94C4.15 8.53 4.09 8.12 4.03 7.71C3.98 7.31 3.88 6.7 3.85 6.5" stroke-width="1.5"/><path d="M5.9 11.5a1.35 1.35 0 0 0 1.55 1.2" stroke-width="1.5"/><path d="M19.22 13.12L19.46 11.94" stroke-width="1.35"/><path d="M21.34 13.73L22.24 12.93" stroke-width="1.35"/><path d="M22.1 16.76L23.13 17.38" stroke-width="1.35"/>'
    };
    var ICON = {};
    Object.keys(ICON_PATHS).forEach(function (k) { ICON[k] = SVG(ICON_PATHS[k]); });
    function opt(cls, html, onClick) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'iq__opt' + (cls ? ' ' + cls : '');
      b.innerHTML = html;
      b.addEventListener('click', onClick);
      return b;
    }
    function setHead(screen, q, hint) {
      var s = root.querySelector('[data-iq-screen="' + screen + '"]');
      s.querySelector('[data-iq-title]').textContent = q;
      s.querySelector('[data-iq-sub]').textContent = hint || '';
    }

    setHead('size', CFG.copy.q1.q, CFG.copy.q1.hint);
    setHead('act',  CFG.copy.q2.q, CFG.copy.q2.hint);
    setHead('need', CFG.copy.q3.q, CFG.copy.q3.hint);
    $('[data-iq-loadt]').textContent = CFG.copy.loading;
    $('[data-iq-badge]').textContent = CFG.copy.badge;
    $('[data-iq-restart]').textContent = CFG.copy.restart;
    root.querySelectorAll('[data-iq-back]').forEach(function (b) {
      b.innerHTML = '← ' + CFG.copy.back;
      b.addEventListener('click', back);
    });

    // Wide toe box toggle. Above the size tiles because a size tap advances.
    var wtbBox = $('[data-iq-wtb]');
    if (wtbBox) {
      $('[data-iq-wtb-l]').textContent = CFG.copy.wtbToggle || 'My shoes have a wide toe box';
      $('[data-iq-wtb-h]').textContent = CFG.copy.wtbToggleHint || '';
      wtbBox.checked = false;
      wtbBox.addEventListener('change', function () {
        A.wtb = !!wtbBox.checked;
        push('quiz_question_answered', { question_id: 'wide_toe_box', answer_value: A.wtb ? 'yes' : 'no' });
      });
    }

    var gSize = $('[data-iq-grid="size"]');
    CFG.sizes.forEach(function (s, i) {
      var short = s.label.replace(/Men's /, 'M ').replace(/ \| Women's /, ' / W ');
      gSize.appendChild(opt('iq__opt--size', '<b>' + short + '</b>', function () {
        A.size = i;
        push('quiz_question_answered', { question_id: 'size', answer_value: s.label });
        go('act');
      }));
    });

    var gAct = $('[data-iq-grid="act"]');
    CFG.activities.forEach(function (a) {
      gAct.appendChild(opt('', '<span class="iq__glyph">' + (ICON[a.icon] || '') + '</span><b>' + a.label + '</b>' + (a.note ? '<span>' + a.note + '</span>' : ''), function () {
        A.act = a.id; A.actLabel = a.label;
        // Only a tile WITHOUT a sub-question is itself the leaf. Pre-filling this
        // put the generic parent ("Court Sports", "Running", "Everyday") into the
        // activity column on rows abandoned at the sub-question, so a pickleball
        // player could land in either bucket and both read smaller than they are.
        A.leafLabel = a.sub ? null : a.label;
        push('quiz_question_answered', { question_id: 'activity', answer_value: a.id });
        if (a.sub) {
          setHead('sub', a.sub.q, a.sub.hint);
          var g = $('[data-iq-grid="sub"]');
          g.innerHTML = '';
          a.sub.opts.forEach(function (o) {
            g.appendChild(opt('', (o.icon ? '<span class="iq__glyph">' + (ICON[o.icon] || '') + '</span>' : '') + '<b>' + o.label + '</b>', function () {
              A.leaf = o.id; A.leafLabel = o.label;
              push('quiz_question_answered', { question_id: 'activity_sub', answer_value: o.id });
              go('need');
            }));
          });
          go('sub');
        } else {
          A.leaf = a.id; A.leafLabel = a.label;
          go('need');
        }
      }));
    });

    var gNeed = $('[data-iq-grid="need"]');
    CFG.needs.forEach(function (n) {
      gNeed.appendChild(opt('iq__opt--need', (n.icon ? '<span class="iq__glyph">' + (ICON[n.icon] || '') + '</span>' : '') + '<span class="iq__opt-txt"><b>' + n.label + '</b><span>' + n.sub + '</span></span>', function () {
        A.need = n.id; A.needLabel = n.sub;
        push('quiz_question_answered', { question_id: 'foot_need', answer_value: n.id });
        resolve();
      }));
    });

    /* ---------- resolution ---------- */
    function needById(id) { return CFG.needs.filter(function (n) { return n.id === id; })[0]; }

    // Substitution ladder. The arch is what the foot-need answer asked for, so it
    // is the thing we protect first: we will cross to the other product to keep
    // the arch before we will change the arch to keep the product (Chuck,
    // 2026-09-01). Only when an arch exists nowhere at this size do we step it
    // toward the middle and sweep again. This is what stops Men's 16-16.5 and
    // 17-17.5 dead-ending: Performance only carries Max Cushion / Low up there.
    //
    // rules[leaf].stay pins a leaf inside its own product no matter what. Only
    // "dress" carries it, because a dress shoe wants the Performance last, not a
    // grippier court insole it has no room for.
    //
    // Returns null only when nothing at all is buyable at this size, which for
    // perf/ns means the whole size is out. The Carbon Elite has no builds to
    // step through, so it returns null the moment its one variant is sold out
    // and the caller falls through to the notify-me state.
    function resolveVariant(pk, prof, arch, size, leaf) {
      var exact = lookup(pk, prof, arch, size);
      if (exact && exact.av) {
        return { pk: pk, v: exact, profile: prof, arch: arch, fallback: false, reason: null };
      }
      if (pk === 'ce') return null;

      var rule = CFG.rules[leaf] || {};
      var base = prof || 'Max Cushion';
      var otherProf = base === 'Max Cushion' ? 'Low Profile' : 'Max Cushion';
      // WTB never crosses: a standard-width insole is not a substitute for fit.
      var pks = (rule.stay || pk === 'wtb') ? [pk] : [pk, (pk === 'perf' ? 'ns' : 'perf')];
      // Wanted arch first, then the two others ordered by how close they sit.
      var arches = [arch].concat(
        arch === 'High' ? ['Medium', 'Low'] : arch === 'Low' ? ['Medium', 'High'] : ['High', 'Low']
      );

      for (var i = 0; i < arches.length; i++) {
        for (var j = 0; j < pks.length; j++) {
          var profs = NOPROF[pks[j]] ? [null] : [base, otherProf];
          for (var k = 0; k < profs.length; k++) {
            var v = lookup(pks[j], profs[k], arches[i], size);
            if (v && v.av) {
              return { pk: pks[j], v: v, profile: profs[k], arch: arches[i],
                       fallback: true, reason: exact ? 'oos' : 'none' };
            }
          }
        }
      }
      return null;
    }

    // "Max Cushion / High arch", or the same with the product in front when the
    // substitution crossed products and the name alone would not explain it.
    function buildLabel(pk, prof, arch, withProduct) {
      if (pk === 'ce') return CAT.ce.title;
      var s = prof ? prof + ' / ' + arch + ' arch' : arch + ' arch';
      return withProduct ? CAT[pk].title + ', ' + s : s;
    }

    // Resolve from what the answers ASKED for (A.want), never from what a previous
    // pass already substituted, so re-rendering cannot quietly launder a
    // substitution into an exact match and drop the explanation.
    function applyResolution() {
      var w = A.want || { pk: A.product, prof: A.profile, arch: A.arch };
      A.want = w;

      var r = resolveVariant(w.pk, w.prof, w.arch, A.size, A.leaf);
      if (!r) {
        A.product = w.pk; A.profile = w.prof; A.arch = w.arch;
        A.fallback = null; A.fallbackReason = null;
        return { swapped: false, first: '', now: '' };
      }

      A.product = r.pk; A.profile = r.profile; A.arch = r.arch;
      var swapped = r.pk !== w.pk;
      var first = w.pk === 'ce' ? 'This build' : buildLabel(w.pk, w.prof, w.arch, swapped);
      var now = buildLabel(r.pk, r.profile, r.arch, swapped);

      if (r.fallback) {
        A.fallbackReason = r.reason;
        A.fallback = first + ' to ' + now + ' (' + (r.reason === 'oos' ? 'sold out' : 'not made') + ')';
      } else {
        A.fallbackReason = null;
        A.fallback = null;
      }
      return { swapped: swapped, first: first, now: now };
    }

    // Every leaf the activity question can end on, flattened, so a second pair can
    // be resolved from a leaf id alone.
    var LEAVES = {};
    CFG.activities.forEach(function (a) {
      if (a.sub) {
        a.sub.opts.forEach(function (o) {
          LEAVES[o.id] = { label: o.label, icon: o.icon, parent: a.id, parentLabel: a.label };
        });
      } else {
        LEAVES[a.id] = { label: a.label, icon: a.icon, parent: a.id, parentLabel: a.label };
      }
    });

    // THE SECOND PAIR. Same feet, same shoes size, so the arch and the size carry
    // over untouched and only the activity changes. Activity is what picks product
    // and build, so one more answer resolves an entire second pair through the same
    // rules table and the same substitution ladder as the first.
    function resolvePair2(leaf) {
      var need = needById(A.need);
      var rule = CFG.rules[leaf];
      if (!need || !rule || A.size == null) return null;

      var pk, prof, arch, raised = false;
      if (need.override === 'ce') {
        pk = 'ce'; prof = null; arch = null;
      } else {
        pk = rule.p; prof = rule.prof; arch = need.arch;
        raised = !!(rule.archMin && RANK[rule.archMin] > RANK[need.arch]);
        if (raised) arch = rule.archMin;
      }

      var r = resolveVariant(pk, prof, arch, A.size, leaf);
      if (!r) return null;

      // Every activity is offered, always. The discount gate below decides what we
      // PROMISE, never what we show: a work customer who wants a pickleball pair
      // should get one, and hiding it to protect a discount rule would be a worse
      // answer than selling it at full price.
      return {
        leaf: leaf,
        label: (LEAVES[leaf] || {}).label || leaf,
        pk: r.pk, profile: r.profile, arch: r.arch,
        archRaised: raised,
        v: r.v,
        fallback: r.fallback ? (r.reason === 'oos' ? 'sold out' : 'not made') : null
      };
    }

    /* ---------- pack pricing ----------------------------------------------
       The quantity break is a Shopify discount; nothing here applies it. These
       functions PREDICT what the cart will do, and they have to model the right
       regime or the screen promises a price checkout will not honour.

         packCrossProduct OFF (today, Kaching): units are counted WITHIN each
           product, which is why one Performance pair plus one NonSlip pair earns
           nothing, while two Performance pairs earn the two-pair tier.
         packCrossProduct ON  (native cart-wide): every unit in the pack counts
           toward one tier.

       One unit is discounted and then summed, never pack-total-divided-by-size.
       Every insole price is a multiple of $5, so 15% and 25% land exact. */
    function pct(v, dflt) {
      v = (v == null || isNaN(v)) ? dflt : Number(v);
      return Math.max(0, Math.min(v, 90)) / 100;
    }
    // Only products the Shopify discount actually entitles count toward a tier.
    // Carbon Elite is in neither "N-Pair Pack" discount, so it earns nothing.
    function packEligible(pk) {
      var c = (CFG.products || {})[pk];
      return !c || c.packEligible !== false;
    }
    function tierFor(units) {
      if (units >= 3) return pct(SET.packTier3Pct, 25);
      if (units >= 2) return pct(SET.packTier2Pct, 15);
      return 0;
    }
    function packMax() {
      var m = Number(SET.packMax);
      return (m === 2 || m === 3) ? m : 3;
    }

    // Every unit in the pack, pair one first. Carbon Elite extra pairs have no
    // variant of their own: they are the same insole again.
    // The pairs actually IN the pack. A.pairs keeps everything the customer has ever
    // chosen, so stepping 3 -> 2 -> 3 restores the third pair instead of asking again.
    function activePairs() {
      return (A.pairs || []).slice(0, Math.max(A.pack - 1, 0));
    }

    function packUnits() {
      var cur = lookup(A.product, A.profile, A.arch, A.size);
      if (!cur) return [];
      var out = [{ pk: A.product, price: cur.price, v: cur }];
      activePairs().forEach(function (p) {
        var v = p.v || cur;
        out.push({ pk: p.v ? p.pk : A.product, price: v.price, v: v });
      });
      return out;
    }

    function packQuote() {
      var units = packUnits();
      var full = 0, net = 0;
      function apply(group, rate) {
        group.forEach(function (u) { full += u.price; net += Math.round(u.price * (1 - rate)); });
      }
      // Ineligible units are charged in full AND do not count toward the tier, which
      // is how a minimum-quantity discount scoped to named products behaves.
      var ok = units.filter(function (u) { return packEligible(u.pk); });
      var no = units.filter(function (u) { return !packEligible(u.pk); });
      apply(no, 0);

      if (SET.packCrossProduct) {
        apply(ok, tierFor(ok.length));
      } else {
        var by = {};
        ok.forEach(function (u) { (by[u.pk] = by[u.pk] || []).push(u); });
        Object.keys(by).forEach(function (k) { apply(by[k], tierFor(by[k].length)); });
      }
      return { full: full, net: net, save: full - net, units: units.length };
    }

    // True once every slot in the chosen pack size has an activity behind it, which
    // is the only point at which an exact total can honestly be quoted.
    function packComplete() {
      if (A.pack < 2) return true;
      // The Carbon Elite has no activities to pick, so a pack of it is complete the
      // moment the size is chosen. Without this the pack never adds more than one.
      if (A.product === 'ce') return true;
      return (A.pairs || []).length >= (A.pack - 1);
    }

    function resolve() {
      var need = needById(A.need);
      var rule = CFG.rules[A.leaf];
      if (!rule) { console.error('[insole-quiz] no rule for leaf ' + A.leaf); return; }

      if (need.override === 'ce') {
        A.product = 'ce'; A.profile = null; A.arch = null; A.archRaised = false;
      } else {
        // The wide toe box toggle swaps the product, never the arch logic. Cleated
        // sports ignore it: there is no wide toe box cleat build.
        var wtbOn = A.wtb && A.act !== 'cleat';
        A.product = wtbOn ? 'wtb' : rule.p;
        A.profile = wtbOn ? null : rule.prof;
        A.arch = need.arch;
        A.archRaised = !!(rule.archMin && RANK[rule.archMin] > RANK[need.arch]);
        if (A.archRaised) A.arch = rule.archMin;
      }

      // Substitute BEFORE the completed event fires, or a size with no variant
      // logs a completed row with a blank variant_id, price and in_stock, which
      // is exactly what the 31 Aug Men's 16-16.5 row in the sheet was.
      A.want = { pk: A.product, prof: A.profile, arch: A.arch };
      applyResolution();

      push('quiz_completed', answerProps());
      go('load');
      window.setTimeout(function () {
        renderResult();
        go('result');
        push('quiz_result_viewed', answerProps());
      }, reduced ? 120 : (SET.processingMs || 900));
    }

    /* ---------- result ---------- */
    function money(cents) {
      if (window.formatMoney && window.money_format) {
        try { return window.formatMoney(cents, window.money_format); } catch (e) {}
      }
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    }

    function renderResult() {
      var need = needById(A.need);
      var rule = CFG.rules[A.leaf];

      // Resolve first: this can move the customer to the other product, and
      // everything below reads A.product.
      var ctx = applyResolution();
      var prod = CAT[A.product];
      var pcfg = CFG.products[A.product];

      $('[data-iq-name]').textContent = prod.title;
      // The activity rationale names the product it was written for, so it can
      // only run when the substitution stayed inside that product.
      $('[data-iq-why]').textContent = A.product === 'ce'
        ? (CFG.copy.whyCE || CE_WHY) + (A.wtb && A.act !== 'cleat' && CFG.copy.wtbCeNote ? ' ' + CFG.copy.wtbCeNote : '')
        : ctx.swapped
          ? (CFG.copy.whySwap || '').replace('{product}', prod.title)
          : A.product === 'wtb'
            ? (CFG.copy.whyWTB || rule.why)
            : rule.why;
      // An injury need's line is written for the Carbon Elite. Once the customer has
      // chosen the lighter insole instead, it would be describing the wrong product.
      var needLine = (need.override === 'ce' && A.product !== 'ce') ? '' : (need.copy || '');
      $('[data-iq-needline]').innerHTML = needLine.replace('{arch}', A.arch || '');
      $('[data-iq-needline]').hidden = !needLine;

      $('[data-iq-bens]').innerHTML = pcfg.benefits.map(function (b) {
        return '<li><span><b>' + b[0] + '</b> ' + b[1] + '</span></li>';
      }).join('');

      // testimonial, only when one has been approved and entered
      var t = (SET.testimonials || {})[A.product] || {};
      var proof = $('[data-iq-proof]');
      if (proof) {
        if (t.quote) {
          proof.hidden = false;
          $('[data-iq-quote]').textContent = t.quote;
          $('[data-iq-cite]').textContent = t.cite || '';
        } else { proof.hidden = true; }
      }

      // Build / Arch / Size — each preselected from the answers, each changeable.
      var hasOptions = A.product !== 'ce';
      $('[data-iq-row-build]').style.display = hasOptions && !NOPROF[A.product] ? '' : 'none';
      $('[data-iq-row-arch]').style.display  = hasOptions ? '' : 'none';
      $('[data-iq-l-build]').textContent = CFG.copy.labelBuild;
      $('[data-iq-l-arch]').textContent  = CFG.copy.labelArch;
      $('[data-iq-l-size]').textContent  = CFG.copy.labelSize;
      // Blank copy means the line is off entirely, not an empty paragraph taking up space.
      var specNote = hasOptions ? CFG.copy.specNote : CFG.copy.specNoteCE;
      $('[data-iq-specnote]').textContent = specNote || '';
      $('[data-iq-specnote]').hidden = !specNote;

      function chips(host, values, current, apply) {
        host.innerHTML = '';
        values.forEach(function (val) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'iq__chip';
          b.textContent = val;
          b.setAttribute('aria-pressed', val === current ? 'true' : 'false');
          var probe = apply(val);
          var v = lookup(A.product, probe[0], probe[1], A.size);
          if (!v || !v.av) {
            b.disabled = true;
            b.title = v ? 'Sold out in your size' : 'Not made in your size';
          }
          b.addEventListener('click', function () {
            var from = lookup(A.product, A.profile, A.arch, A.size);
            // A hand-picked build becomes the new "wanted" combination, so the
            // ladder measures any later substitution against the customer's
            // choice rather than the original recommendation.
            A.want = { pk: A.product, prof: probe[0], arch: probe[1] };
            A.edited = true;
            renderResult();
            var to = lookup(A.product, A.profile, A.arch, A.size);
            push('quiz_variant_edited', { field: host.hasAttribute('data-iq-chips-build') ? 'profile' : 'arch', from_variant: from ? from.id : null, to_variant: to ? to.id : null });
          });
          host.appendChild(b);
        });
      }

      if (hasOptions) {
        if (!NOPROF[A.product]) chips($('[data-iq-chips-build]'), PROFILES, A.profile, function (p) { return [p, A.arch]; });
        chips($('[data-iq-chips-arch]'),  ARCHES,   A.arch,    function (a) { return [A.profile, a]; });
      }

      var sel = $('[data-iq-size]');
      sel.innerHTML = '';
      CFG.sizes.forEach(function (s, i) {
        var v = lookup(A.product, A.profile, A.arch, i);
        var o = document.createElement('option');
        o.value = i;
        o.textContent = s.label + (v ? (v.av ? '' : ' (sold out)') : ' (not made)');
        if (i === A.size) o.selected = true;
        sel.appendChild(o);
      });
      sel.onchange = function () {
        var from = lookup(A.product, A.profile, A.arch, A.size);
        A.want = { pk: A.product, prof: A.profile, arch: A.arch };
        A.size = parseInt(sel.value, 10);
        A.edited = true;
        renderResult();
        var to = lookup(A.product, A.profile, A.arch, A.size);
        push('quiz_variant_edited', { field: 'size', from_variant: from ? from.id : null, to_variant: to ? to.id : null });
      };

      renderPack();

      var cur = lookup(A.product, A.profile, A.arch, A.size);
      var img = $('[data-iq-img]');
      img.src = (cur && cur.img) || prod.image || '';
      img.alt = prod.title;

      var atc = $('[data-iq-atc]');
      var stock = $('[data-iq-stock]');
      // The headline price follows the pack choice, so the number by the button is
      // always the number the customer is about to pay.
      var packOn = A.pack > 1 && packComplete();
      $('[data-iq-price]').textContent = cur
        ? money(packOn ? quoteFor(A.pack, A.product === 'ce', cur).net : cur.price)
        : '—';
      if (cur && cur.av) {
        stock.textContent = CFG.copy.inStock; stock.classList.remove('is-out');
        atc.disabled = false;
        atc.textContent = !packOn ? CFG.copy.atc
          : A.pack > 2 ? (CFG.copy.packAtcN || CFG.copy.packAtc || CFG.copy.atc).replace('{n}', A.pack)
          : (CFG.copy.packAtc || CFG.copy.atc);
      } else {
        stock.textContent = CFG.copy.outStock; stock.classList.add('is-out');
        // No variant at all means there is nothing to add and nothing to notify
        // on, so the button has to be dead rather than throwing an error on click.
        atc.disabled = !cur; atc.textContent = CFG.copy.atcOut;
      }
      $('[data-iq-err]').hidden = true;

      renderCeAlt(need, rule, cur);

      // The substitution message always shows; the standing footnote only if one is set.
      var note = A.fallback
        ? (A.fallbackReason === 'oos' ? CFG.copy.fallbackOos : CFG.copy.fallbackNone)
            .replace('{first}', ctx.first).replace('{now}', ctx.now)
        : CFG.copy.footnote;
      $('[data-iq-note]').textContent = note || '';
      $('[data-iq-note]').hidden = !note;
      $('[data-iq-note]').classList.toggle('is-sub', !!A.fallback);
    }

    /* ---------- the Carbon Elite's lighter option (RR-024, 2026-10-05) ----------
       The Carbon Elite is the only result for turf toe and a Lisfranc sprain, and at
       $140 against $55 to $65 it reached the cart at about half the rate of every other
       result (in stock: 7 of 73 runs, against 162 of 741). Two changes, read together
       as one on that result's cart rate: the why line says what the extra money buys,
       and the insole the customer's activity would otherwise get sits beside it with
       its price, one tap away. Choosing it keeps the add inside the quiz, so the run
       is still counted. Nothing here changes what the quiz recommends. */
    var CE_WHY = 'Cushioning alone does not protect an injured toe or midfoot. The Carbon Elite adds a carbon plate that limits how far the foot bends.';
    var CE_ALT = 'Want to start lighter? For {activity}, our {alt} are {price} in your size.';
    var CE_ALT_BTN = 'Show me that one';
    var CE_BACK = 'Back to the {ce}';
    var CE_ALT_ARCH = 'Medium';   // injury answers skip the arch question; Medium is the middle chip

    function ceAltWant(rule) {
      if (!rule || !rule.p || rule.p === 'ce') return null;
      return { pk: rule.p, prof: rule.prof, arch: CE_ALT_ARCH };
    }

    function renderCeAlt(need, rule, cur) {
      var buy = root.querySelector('.iq__buy');
      if (!buy) return;
      var el = root.querySelector('[data-iq-cealt]');
      if (!el) {
        el = document.createElement('p');
        el.className = 'iq__foot iq__cealt';
        el.setAttribute('data-iq-cealt', '');
        buy.parentNode.insertBefore(el, buy.nextSibling);
      }
      el.innerHTML = '';
      el.hidden = true;
      if (!need || need.override !== 'ce') return;
      var want = ceAltWant(rule);
      if (!want) return;

      var link = document.createElement('button');
      link.type = 'button';
      link.className = 'iq__restart';
      if (A.product === 'ce') {
        var alt = lookup(want.pk, want.prof, want.arch, A.size);
        if (!alt || !alt.av) return;   // nothing in stock to offer, so offer nothing
        el.appendChild(document.createTextNode(
          (CFG.copy.ceAlt || CE_ALT)
            .replace('{alt}', CAT[want.pk].title)
            .replace('{activity}', (A.leafLabel || 'your activity').toLowerCase())
            .replace('{price}', money(alt.price)) + ' '));
        link.textContent = CFG.copy.ceAltBtn || CE_ALT_BTN;
        link.addEventListener('click', function () {
          A.want = want; A.edited = true; A.ceAlt = true;
          A.pack = 1; A.pairs = [];
          renderResult();
          push('quiz_ce_alt_selected', answerProps());
        });
      } else if (A.ceAlt) {
        link.textContent = (CFG.copy.ceBack || CE_BACK).replace('{ce}', CAT.ce.title);
        link.addEventListener('click', function () {
          A.want = { pk: 'ce', prof: null, arch: null }; A.ceAlt = false;
          A.pack = 1; A.pairs = [];
          renderResult();
          push('quiz_ce_alt_reverted', answerProps());
        });
      } else {
        return;
      }
      el.appendChild(link);
      el.hidden = false;
    }

    /* ---------- the extra pairs ----------
       Same feet, same size, so the arch and the size carry over and only the
       activity changes. One tap per extra pair, two where the tile has a
       sub-question. EVERY activity is offered: the discount regime decides what is
       promised, never what is shown, because hiding pickleball from a work
       customer to protect a discount rule is a worse answer than selling it. */
    function renderPack() {
      var host = $('[data-iq-pack]');
      if (!host || SET.packEnabled === false) return;

      var cur = lookup(A.product, A.profile, A.arch, A.size);
      if (!cur || !cur.av) { host.hidden = true; A.pack = 1; A.pairs = []; return; }

      // A product the discount does not entitle earns nothing at any quantity, so
      // there is no pack worth offering and no saving worth claiming. Today that is
      // the Carbon Elite, which sits outside both "N-Pair Pack" discounts.
      if (!packEligible(A.product)) { host.hidden = true; A.pack = 1; A.pairs = []; return; }

      // Otherwise: a product with no builds resolves every activity to the same
      // variant, so there is no question worth asking, just more pairs.
      var ceMode = A.product === 'ce';

      // A size or build edit can invalidate pairs chosen against the old one.
      A.pairs = (A.pairs || []).map(function (p) { return resolvePair2(p.leaf); })
                               .filter(Boolean);

      var parents = packParents(ceMode);
      if (!ceMode && !parents.length) { host.hidden = true; A.pack = 1; A.pairs = []; return; }
      if (A.pack > packMax()) { A.pack = packMax(); }
      host.hidden = false;

      var subKey = SET.packCrossProduct ? 'packSubAll' : 'packSub';
      $('[data-iq-pack-title]').textContent = CFG.copy.packTitle || '';
      $('[data-iq-pack-sub]').textContent = (CFG.copy[subKey] || CFG.copy.packSub || '')
        .replace('{pct}', Math.round(tierFor(2) * 100));

      // One pair stays the default, so ignoring this row leaves the screen behaving
      // exactly as it did before the row existed.
      var opts = $('[data-iq-pack-opts]');
      opts.innerHTML = '';
      var sizes = packMax() === 3 ? [1, 2, 3] : [1, 2];
      opts.setAttribute('data-n', String(sizes.length));

      sizes.forEach(function (n) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'iq__pack-opt' + (A.pack === n ? ' is-on' : '');
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', A.pack === n ? 'true' : 'false');

        var lab = document.createElement('b');
        lab.textContent = (CFG.copy.packN || '{n} pairs').replace('{n}', n);
        if (n === 1) { lab.textContent = CFG.copy.packOne || '1 pair'; }
        b.appendChild(lab);

        // Money only when every slot is filled. A total guessed from the pairs
        // already chosen can RISE once a dearer build is picked, and a price that
        // goes up after the customer commits is worse than no price at all.
        var exact = n === 1 || ceMode || (A.pack === n && packComplete());
        var pr = document.createElement('span');
        var sv = document.createElement('em');

        if (exact) {
          var q = n === 1 ? { net: cur.price, save: 0 } : quoteFor(n, ceMode, cur);
          pr.textContent = money(q.net);
          sv.textContent = q.save > 0
            ? (CFG.copy.packSave || 'You save {amount}').replace('{amount}', money(q.save))
            : '';
        } else {
          pr.textContent = '';
          sv.textContent = (CFG.copy.packSaveUpTo || 'Save up to {pct}%')
            .replace('{pct}', Math.round(tierFor(n) * 100));
        }
        if (pr.textContent) { b.appendChild(pr); }
        if (sv.textContent) { b.appendChild(sv); }

        b.addEventListener('click', function () {
          if (A.pack === n) return;
          A.pack = n;
          A.packParent = null;
          if (ceMode) { A.pairs = []; }
          // Nothing is discarded here. Pairs beyond the new size stay remembered and
          // come back the moment the customer steps up again.
          push('quiz_pack_selected', Object.assign({ pack_size: n }, answerProps()));
          renderResult();
        });
        opts.appendChild(b);
      });

      var needed = ceMode ? 0 : Math.max(A.pack - 1, 0);
      var wantPicker = activePairs().length < needed;

      $('[data-iq-pack-pick]').hidden = !wantPicker;
      if (wantPicker) { renderPackPicker(parents, activePairs().length + 2); }

      renderPackList(cur);

      // Only explain the discount when the customer is actually losing one, and only
      // once a full pack is on screen to explain it about.
      var note = $('[data-iq-pack-note]');
      var showNote = false;
      if (A.pack > 1 && packComplete() && !SET.packCrossProduct) {
        var q = packQuote();
        var pks = {};
        packUnits().forEach(function (u) { pks[u.pk] = 1; });
        showNote = q.save === 0 && Object.keys(pks).length > 1;
      }
      note.textContent = CFG.copy.packMixedNote || '';
      note.hidden = !(showNote && note.textContent);
    }

    // What a pack of n would cost, given what has been chosen so far.
    function quoteFor(n, ceMode, cur) {
      if (ceMode) {
        var r = packEligible(A.product) ? tierFor(n) : 0;
        var unit = Math.round(cur.price * (1 - r));
        return { net: unit * n, save: (cur.price * n) - (unit * n) };
      }
      return packQuote();
    }

    // Every activity except the one they already answered. A leaf drops out only if
    // nothing is buyable for it in this size.
    function packParents(ceMode) {
      if (ceMode) return [];
      var mine = (LEAVES[A.leaf] || {}).parent;
      return CFG.activities.filter(function (a) {
        if (a.id === mine) return false;
        return packLeaves(a).length > 0;
      });
    }
    function packLeaves(a) {
      var ids = a.sub ? a.sub.opts.map(function (o) { return o.id; }) : [a.id];
      return ids.filter(function (id) { return !!resolvePair2(id); });
    }

    function ordinal(n) {
      var words = CFG.copy.packOrdinals || ['Second', 'Third', 'Fourth'];
      return words[n - 2] || ('Pair ' + n);
    }

    function renderPackPicker(parents, pairNo) {
      var grid = $('[data-iq-pack-grid]');
      var parent = A.packParent
        ? parents.filter(function (a) { return a.id === A.packParent; })[0]
        : null;

      $('[data-iq-pack-q]').textContent = parent
        ? (parent.sub ? parent.sub.q : (CFG.copy.packSubQ || 'Which one?'))
        : pairNo > 2 ? (CFG.copy.packQNext || CFG.copy.packQ || '')
        : (CFG.copy.packQ || '');
      $('[data-iq-pack-hint]').textContent = parent ? '' : (CFG.copy.packQHint || '');

      grid.innerHTML = '';
      if (!parent) {
        parents.forEach(function (a) {
          grid.appendChild(opt('iq__pack-tile',
            '<span class="iq__glyph">' + (ICON[a.icon] || '') + '</span><b>' + a.label + '</b>',
            function () {
              var leaves = packLeaves(a);
              if (!a.sub || leaves.length === 1) { addPair(leaves[0]); }
              else { A.packParent = a.id; renderResult(); }
            }));
        });
        return;
      }

      grid.appendChild(opt('iq__pack-tile iq__pack-back', '<b>← ' + (CFG.copy.back || 'Back') + '</b>',
        function () { A.packParent = null; renderResult(); }));
      parent.sub.opts.forEach(function (o) {
        if (!resolvePair2(o.id)) return;
        grid.appendChild(opt('iq__pack-tile',
          '<span class="iq__glyph">' + (ICON[o.icon] || '') + '</span><b>' + o.label + '</b>',
          function () { addPair(o.id); }));
      });
    }

    function addPair(leaf) {
      var p = resolvePair2(leaf);
      if (!p) return;
      // The picker only opens when the kept list is SHORTER than the pack needs, so
      // the empty slot is always the end of the list.
      A.pairs.push(p);
      A.packParent = null;
      if (A.pack < activePairs().length + 1) { A.pack = activePairs().length + 1; }
      push('quiz_pack_pair_selected', {
        pair_no: activePairs().length + 1,
        pair_activity: leaf,
        pair_product: CAT[p.pk].title,
        pair_variant: p.v ? p.v.id : null,
        pair_crossed: p.pk !== A.product
      });
      renderResult();
    }

    function renderPackList(cur) {
      var list = $('[data-iq-pack-list]');
      list.innerHTML = '';
      var ceMode = A.product === 'ce';

      if (ceMode && A.pack > 1) {
        var r = document.createElement('p');
        r.className = 'iq__pack-same';
        r.textContent = (CFG.copy.packCeExtra || '{n} pairs of the same insole.')
          .replace('{n}', A.pack);
        list.appendChild(r);
        list.hidden = false;
        return;
      }

      var shown = activePairs();
      list.hidden = !shown.length;
      shown.forEach(function (p, i) {
        var row = document.createElement('div');
        row.className = 'iq__pack-row';

        var src = (p.v && p.v.img) || CAT[p.pk].image || '';
        if (src) {
          var im = document.createElement('img');
          im.className = 'iq__pack-thumb';
          im.src = src; im.alt = CAT[p.pk].title;
          im.width = 52; im.height = 52;
          im.loading = 'lazy'; im.decoding = 'async';
          row.appendChild(im);
        }

        var txt = document.createElement('span');
        txt.className = 'iq__pack-two-txt';
        var nm = document.createElement('b');
        nm.textContent = ordinal(i + 2) + ' ' + (CFG.copy.packPairWord || 'pair') + ': ' + p.label;
        var sp = document.createElement('span');
        var same = p.v && cur && p.v.id === cur.id;
        sp.textContent = same
          ? (CFG.copy.packSameBuild || '')
          : buildLabel(p.pk, p.profile, p.arch, true);
        txt.appendChild(nm); txt.appendChild(sp);
        row.appendChild(txt);

        var ch = document.createElement('button');
        ch.type = 'button';
        ch.className = 'iq__pack-change';
        ch.textContent = CFG.copy.packChange || 'Change';
        ch.addEventListener('click', function () {
          A.pairs.splice(i, 1);
          A.packParent = null;
          renderResult();
        });
        row.appendChild(ch);
        list.appendChild(row);
      });
    }

    /* ---------- add to cart ---------- */
    function toast(msg) {
      var t = $('[data-iq-toast]');
      t.textContent = msg;
      t.classList.add('is-on');
      window.setTimeout(function () { t.classList.remove('is-on'); }, 2600);
    }
    function showError(msg) {
      var e = $('[data-iq-err]');
      e.textContent = msg;
      e.hidden = false;
    }

    $('[data-iq-atc]').addEventListener('click', function () {
      var btn = this;
      var cur = lookup(A.product, A.profile, A.arch, A.size);
      if (!cur) { showError('That combination is not available. Pick another from the list above.'); return; }

      if (!cur.av) {
        // hand off to the theme's existing back-in-stock modal where present
        push('quiz_cart_error', Object.assign({ reason: 'sold_out' }, answerProps()));
        var bis = document.querySelector('[data-back-in-stock], .js-back-in-stock');
        if (bis) { bis.click(); return; }
        window.location.href = CAT[A.product].url + '?variant=' + cur.id;
        return;
      }

      btn.disabled = true;
      var label = btn.textContent;
      btn.textContent = CFG.copy.atcBusy;
      $('[data-iq-err]').hidden = true;

      function done() { btn.disabled = false; btn.textContent = label; }
      function success() {
        done();
        push('quiz_add_to_cart', packProps());
        toast('Added to your cart');
      }
      function failure(msg) {
        done();
        showError(msg || 'We could not add that to your cart. Try again, or open the product page.');
        push('quiz_cart_error', Object.assign({ reason: 'add_failed' }, packProps()));
      }

      /*
        Attribution. Every key is underscore-prefixed, so Shopify treats it as a
        hidden line-item property: it never renders in the cart or at checkout, but
        it is stored permanently on the order and comes back in the Orders API, the
        admin order page and any order export. This is what makes quiz-attributed
        revenue answerable without a backend of any kind - and it cannot be
        backfilled, so it has to ship with the quiz, not after it.
      */
      /*
        Attribution. Every key is underscore-prefixed, so Shopify treats it as a
        hidden line-item property: it never renders in the cart or at checkout, but
        it is stored permanently on the order and comes back in the Orders API, the
        admin order page and any order export. This is what makes quiz-attributed
        revenue answerable without a backend of any kind - and it cannot be
        backfilled, so it has to ship with the quiz, not after it.

        On a two-pair add every line carries the same _bundle_id as well, which is
        the bundle identifier the warehouse has no other way to see. Attribute at the
        LINE, never the order: a discount code allocates across lines and summing
        line originals overstates the order.
      */
      var packing = A.pack > 1 && packComplete();
      var bundleId = packing ? newQuizId() : null;
      // On state so the add_to_cart ROW can name the bundle, not just the cart lines.
      A.bundleId = bundleId;

      function propsFor(pairNo, act, raised, fb) {
        var o = {
          _quiz_id: QID,
          _quiz_version: QUIZ_VERSION,
          _quiz_size: A.size != null ? CFG.sizes[A.size].label : '',
          _quiz_activity: act || '',
          _quiz_foot_need: A.needLabel || A.need || '',
          _quiz_arch_raised: raised ? 'yes' : 'no',
          _quiz_fallback: fb || 'none',
          _quiz_edited: A.edited ? 'yes' : 'no'
        };
        if (packing) {
          o._bundle_id = bundleId;
          o._bundle_size = String(A.pack);
          o._bundle_source = 'quiz';
          o._quiz_pair = String(pairNo);
        }
        return o;
      }

      var lines = [{
        id: cur.id,
        quantity: 1,
        properties: propsFor(1, A.leafLabel || A.leaf, A.archRaised, A.fallback)
      }];

      if (packing) {
        if (A.product === 'ce') {
          // No builds to vary, so the whole pack is the same insole again.
          lines[0].quantity = A.pack;
        } else {
          activePairs().forEach(function (p, i) {
            var v = p.v;
            if (!v) return;
            // Two activities can land on the same variant, which is a real purchase
            // (one pair in each bag). Merging keeps the cart tidy and counts the same
            // for the quantity discount.
            var hit = null;
            for (var j = 0; j < lines.length; j++) { if (lines[j].id === v.id) { hit = lines[j]; break; } }
            if (hit) { hit.quantity += 1; return; }
            lines.push({
              id: v.id,
              quantity: 1,
              properties: propsFor(i + 2, p.label, p.archRaised, p.fallback)
            });
          });
        }
      }

      function packProps() {
        return Object.assign({
          pack_size: packing ? A.pack : 1,
          bundle_id: bundleId,
          pack_activities: activePairs().map(function (p) { return p.leaf; }).join(',') || null,
          price: packing ? quoteFor(A.pack, A.product === 'ce', cur).net : cur.price
        }, answerProps());
      }

      /*
        window._ajaxAddToCart is the theme's own path and the only one that opens and
        repopulates the slide-out drawer correctly, but it posts ONE line at a time.
        So two pairs go in sequentially and only the last response drives the drawer.
        A failure on the second line is reported honestly rather than silently: the
        first pair really is in the cart by then.
      */
      function themeAdd(line, isLast, next) {
        var flat = { id: line.id, quantity: line.quantity };
        Object.keys(line.properties).forEach(function (k) {
          flat['properties[' + k + ']'] = line.properties[k];
        });
        var fd = window._createFormDataFromObject(flat);
        window._ajaxAddToCart(fd, function (res) {
          if (res && res.id) {
            if (isLast && typeof window._updateCartTotals === 'function') {
              window._updateCartTotals(res);
            }
            next(null);
          } else {
            next((res && res.description) || true);
          }
        });
      }

      function runThemeAdds(i) {
        themeAdd(lines[i], i === lines.length - 1, function (err) {
          if (err) {
            done();
            if (i > 0) {
              showError(CFG.copy.packPartial || 'Part of your pack is in the cart. We could not add the rest, so try it again.');
            } else {
              showError(typeof err === 'string' ? err : null);
            }
            push('quiz_cart_error', Object.assign({ reason: 'add_failed', failed_line: i + 1 }, packProps()));
            return;
          }
          if (i + 1 < lines.length) { runThemeAdds(i + 1); return; }
          success();
        });
      }

      try {
        if (typeof window._ajaxAddToCart === 'function' && typeof window._createFormDataFromObject === 'function') {
          runThemeAdds(0);
          return;
        }
      } catch (e) { /* fall through to fetch */ }

      // Fallback if the theme's cart bundle has not loaded. /cart/add.js takes every
      // line in one request here, so there is no partial state to explain.
      fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: lines })
      }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (o) {
          if (!o.ok) { failure(o.j && o.j.description); return; }
          success();
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch(function () { failure(); });
    });

    /* ---------- optional email offer ---------- */
    var form = $('[data-iq-emailform]');
    if (form) {
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        var input = form.querySelector('input[type="email"]');
        var msg = $('[data-iq-emailmsg]');
        var email = (input.value || '').trim();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
          msg.hidden = false;
          msg.className = 'iq__email-msg is-err';
          msg.textContent = 'That email address does not look right. Check it and try again.';
          input.focus();
          return;
        }
        var props = answerProps();
        try {
          if (window._learnq) {
            window._learnq.push(['identify', Object.assign({ $email: email }, props)]);
          }
        } catch (e) {}
        push('quiz_email_submitted', Object.assign({ email_consent: true }, props));
        record('email_submitted', { email: email, email_consent: true });

        var listId = root.getAttribute('data-klaviyo-list');
        var companyId = root.getAttribute('data-klaviyo-company');
        var finish = function () {
          msg.hidden = false;
          msg.className = 'iq__email-msg is-ok';
          msg.textContent = SET.offerSuccess || 'Check your inbox.';
          // Show the code inline when one is configured, so the offer is honoured on
          // the spot rather than depending on a Klaviyo flow existing. Built with DOM
          // nodes, not innerHTML, so a merchant-entered value can never inject markup.
          var code = (SET.discountCode || '').trim();
          if (code) {
            msg.appendChild(document.createTextNode(' Use code '));
            var strong = document.createElement('b');
            strong.textContent = code;
            msg.appendChild(strong);
            msg.appendChild(document.createTextNode(' at checkout.'));
          }
          form.hidden = true;
        };

        if (listId && companyId) {
          // Klaviyo client-side subscribe. Public company ID only — no private key here.
          fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  profile: { data: { type: 'profile', attributes: { email: email, properties: props } } }
                },
                relationships: { list: { data: { type: 'list', id: listId } } }
              }
            })
          }).then(finish).catch(finish);
        } else {
          // No list configured yet — the identify + event above still reach Klaviyo,
          // so a flow can pick it up. See section settings.
          finish();
        }
      });
    }

    /* ---------- wiring ---------- */
    $('[data-iq-start]').addEventListener('click', function () {
      push('quiz_started', {});
      go('size');
    });
    $('[data-iq-restart]').addEventListener('click', function () {
      A = freshAnswers();
      if (wtbBox) wtbBox.checked = false;
      history = ['welcome'];
      QID = newQuizId();   // a restart is a new run, not a continuation
      RECORDED = {};       // otherwise the dedupe guard swallows run two
      show('welcome');
    });

    // view + abandonment
    var seen = false;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (e) {
          if (e.isIntersecting && !seen) { seen = true; push('quiz_viewed', {}); obs.disconnect(); }
        });
      }, { threshold: 0.3 }).observe(root);
    } else { push('quiz_viewed', {}); }

    window.addEventListener('pagehide', function () {
      var last = history[history.length - 1];
      if (last !== 'welcome' && last !== 'result') {
        push('quiz_abandoned', Object.assign({ last_question: last }, answerProps()));
      }
    });
  return true;
  }

  function initAll() {
    var n = 0;
    Array.prototype.forEach.call(document.querySelectorAll('[data-iq]'), function (r) {
      if (boot(r)) n++;
    });
    return n;
  }

  window.BlumakaInsoleQuiz = { init: boot, initAll: initAll };
  initAll();
})();
